import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { matchPath } from 'react-router-dom';

import { getApiBaseUrl, shouldBypassAuth } from './api-config';

// Create axios instance without baseURL - we'll set it dynamically per request
const instance = axios.create();
let _tokenFetcher: () => Promise<string>;
let _demoTokenFetcher: () => Promise<string>;

instance.interceptors.request.use(
  async (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    // Set the base URL dynamically based on the request path
    // Only set baseURL if:
    // 1. config.url exists and is not empty
    // 2. config.url is not already a full URL (doesntstart with http:// or https://)
    if (config.url && !/^https?:\/\//.test(config.url)) {
      config.baseURL = getApiBaseUrl(config.url);
    } else if (!config.url) {
      config.baseURL = getApiBaseUrl('/');
    }

    const isDemo =
      matchPath({ path: '/demo/t/:tenantId/*' }, location.pathname) ||
      matchPath({ path: '/demo/redirect' }, location.pathname);

    const isWhitelisted = shouldBypassAuth(config.url || '');
    if (!isWhitelisted && ((isDemo && !_demoTokenFetcher) || (!isDemo && !_tokenFetcher))) {
      return Promise.resolve({
        ...config,
        cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
          cancel('Axios API was not initialized with the appropriate token fetching function.'),
        ),
      } as unknown as InternalAxiosRequestConfig);
    }
    try {
      if (isWhitelisted) {
        return Promise.resolve({
          ...config,
          headers: { ...config.headers },
        } as unknown as InternalAxiosRequestConfig);
      }
      const jwt = isDemo ? await _demoTokenFetcher() : await _tokenFetcher();
      return Promise.resolve({
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${jwt}` },
      } as unknown as InternalAxiosRequestConfig);
    } catch (e) {
      return Promise.resolve({
        ...config,
        cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
          cancel('No access token!'),
        ),
      } as unknown as InternalAxiosRequestConfig);
    }
  },
  (error: Error) => Promise.reject(error),
);

export default instance;

/**
 * Provide the ability to pass the getAccessTokenSilently function from Auth0 when the app bootstraps.
 * @param tokenFetcher
 */
export const setTokenFetcher = (tokenFetcher: () => Promise<string>) => {
  _tokenFetcher = tokenFetcher;
  return this;
};

export const setDemoTokenFetcher = (tokenFetcher: () => Promise<string>) => {
  _demoTokenFetcher = tokenFetcher;
  return this;
};
